/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {AspectRatio, Badge, Box, Image} from '@salesforce/retail-react-app/app/components/shared/ui';
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils';

import {useItemVariant} from './';

/**
 * In the context of a cart product item variant, this component renders the item's
 * main image.
 *
 * @todo = This component will render a `SALE` badge when the qualifier is available
 * on the custom property `c_isSale`. This will need to be expanded upon to handle
 * different badge/qualifiers and property names.
 */
const ItemImage = ({imageProps, ratio = 1, ...props}) => {
  const variant = useItemVariant();

  // We find the 'small' images in the variant's image groups based on variationValues and pick the first one
  const image = findImageGroupBy(variant?.imageGroups, {
    viewType: 'small',
    selectedVariationAttributes: variant?.variationValues,
  })?.images?.[0];

  return (
    <Box backgroundColor="gray.100" width="84px" {...props}>
      <AspectRatio ratio={ratio}>
        <Box position="relative">
          {variant.c_isSale && (
            <Badge
              colorScheme="blue"
              fontSize="10px"
              left={0}
              marginLeft={2}
              marginTop={2}
              position="absolute"
              top={0}
              variant="solid"
            >
              <FormattedMessage
                defaultMessage="Sale"
                description="A sale badge placed on top of a product image"
                id="item_image.label.sale"
              />
            </Badge>
          )}
          {variant.isProductUnavailable && (
            <Badge
              colorScheme="red"
              fontSize="10px"
              left={0}
              marginLeft={2}
              marginTop={2}
              position="absolute"
              top={0}
              variant="solid"
            >
              <FormattedMessage
                defaultMessage="Unavailable"
                description="A unavailable badge placed on top of a product image"
                id="item_image.label.unavailable"
              />
            </Badge>
          )}

          {image && (
            <Image
              alt={image.alt}
              ignoreFallback={true}
              src={`${image.disBaseLink || image.link}`}
              {...imageProps}
            />
          )}
        </Box>
      </AspectRatio>
    </Box>
  );
};

ItemImage.propTypes = {
  imageProps: PropTypes.object,
  ratio: PropTypes.number,
};

export default ItemImage;
